import workspaceService from "api/workspaceService";
import { ENotificationType, notification } from "utils/notification";
import WorkspaceMembers from "components/WorkspaceMembers";
import AddWorker from "components/Settings/AddWorker";
import { IWorkspace, IWorkspaceMember } from "dto/IWorkspace";
import { getUserId } from "utils/getUserId";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getPrivilages } from "utils/getPrivilages";
import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";
import styles from "./styles.module.scss";
import TeamIcon from "assets/icons/team.svg?react";
import Spinner from "components/Spinner";
import { useAuth } from "react-oidc-context";
import { Claims } from "dto/User/claims";
import { getUserClaim } from "utils/getUserClaim";
import { EUserType } from "dto/User/EUserType";
import OrgChart, {
  getOrgChartPositions,
  getOrgChartSupervisorMap,
  setOrgPersonStatus,
  MemberStatus,
  OrgNode,
  loadTree,
  saveTree,
  findNode,
  updateNode,
  removeNode,
  collectPeople,
  collectUnits,
  findParent,
  moveNodeFn,
  EditPanel,
  MoveDialog,
} from "./OrgChart";
import StructureEditor from "./StructureEditor";
import AddPersonPanel from "./AddPersonPanel";
import PrivilegesTab from "./PrivilegesTab";
import KadroweTab from "../DictionariesTab/KadroweTab";
import { loadPrivileges } from "./PrivilegesTab/privilegesUtils";
import { flattenStructure, loadStructure } from "./StructureEditor/treeUtils";
import { InputAdornment, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CreateWorkspace from "components/CreateWorkspace";

type SubTab = "members" | "structure" | "privileges" | "orgchart" | "kadry";

const STATUS_CONFIG: Record<
  MemberStatus,
  { label: string; bg: string; color: string }
> = {
  wpisany: { label: "Wpisany", bg: "#f5f5f5", color: "#757575" },
  aktywuje: { label: "Zaproszono", bg: "#fff3e0", color: "#e65100" },
  aktywny: { label: "Aktywny", bg: "#e8f5e9", color: "#2e7d32" },
  archiwalny: { label: "Archiwalny", bg: "#efebe9", color: "#795548" },
};

const WorkersTab = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const isLegalPerson =
    getUserClaim(auth.user, Claims.UserTypeClaim) === EUserType.LegalPerson;

  const [subTab, setSubTab] = useState<SubTab>("members");
  const [searchOrg, setSearchOrg] = useState("");
  // showNewAdd — for new expanded AddWorker (org chart add + invite)
  // showAdd / setShowAdd — passed to WorkspaceMembers but intercepted so it also opens new AddWorker
  const [showNewAdd, setShowNewAdd] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [invitePersonId, setInvitePersonId] = useState<string | null>(null);
  const [selectedOrgPersonId, setSelectedOrgPersonId] = useState<string | null>(
    null,
  );
  const [orgTree, setOrgTree] = useState<OrgNode[]>(() => loadTree());
  const [moveNodeId, setMoveNodeId] = useState<string | null>(null);
  const [showAddPersonPanel, setShowAddPersonPanel] = useState(false);

  // D1 (model-kluczy-firmowych): jedna firma na użytkownika, bez przełącznika kontekstu —
  // workspaceId czytamy wprost z mirrora firmy (bez pośredniczącego UserProfileContext).
  // `workspaceIdOverride` to awaryjny nadpis na czas między utworzeniem zespołu
  // (CreateWorkspace, patrz handleCreateWorkspace) a odświeżeniem tego mirrora — bez niego
  // komponent nie wiedziałby, że ma przestać renderować ekran „Załóż zespół".
  const [workspaceIdOverride, setWorkspaceIdOverride] = useState<string | null>(
    null,
  );
  const workspaceId = workspaceIdOverride ?? getWorkspaceId() ?? "";
  const {
    data: workspaceData,
    refetch,
    isLoading: isLoadingWorkspace,
  } = workspaceService.useGetWorkspace(workspaceId);

  const workspace: IWorkspace = useMemo(
    () => (workspaceData?.data as IWorkspace) ?? null,
    [workspaceData],
  );

  const workspaceMembers = useMemo(() => workspace?.Members ?? [], [workspace]);
  const workspaceRoles = useMemo(() => workspace?.Roles ?? [], [workspace]);

  const me = workspaceMembers.find((membership) => membership.IsMe === true);
  const meRole = workspaceRoles.find((role) => role.Id === me?.RoleId);
  const myPrivilages = getPrivilages(meRole);

  const privileges = loadPrivileges();
  const meUserId = me?.UserId ?? "";
  const canInvite =
    !!me?.IsOwner ||
    privileges.partners.includes(meUserId) ||
    privileges.admins.includes(meUserId);

  // „Właściciel konta" — gdy konto nie ma jeszcze firmy (D2: konto może istnieć bez
  // firmy), właścicielem sekcji jest zalogowany posiadacz konta. Prezentujemy go
  // automatycznie, aby nie musiał być nigdzie ręcznie wpisywany. W praktyce nieosiągalne
  // niżej w drzewie — komponent wraca wcześniej z ekranem „Załóż zespół" (patrz `if
  // (!workspaceId)` na końcu), zostawione jako dokumentacja intencji dla PrivilegesTab.
  const accountOwner: IWorkspaceMember | undefined =
    !workspaceId && auth.user
      ? {
          Id: "",
          StructureId: "",
          UserId: getUserId(auth.user),
          Label:
            (auth.user.profile?.name as string) ??
            getUserClaim(auth.user, Claims.Email) ??
            "",
          RoleId: "",
          Status: 0,
          IsMe: true,
          IsOwner: true,
          Email: getUserClaim(auth.user, Claims.Email) || undefined,
        }
      : undefined;

  const orgPeople = useMemo(() => {
    if (subTab !== "members") return [];
    const all = getOrgChartPositions();
    const filled = all
      .filter((p) => !!p.personFullName)
      .sort((a, b) => {
        const aSurname = (a.personFullName ?? "").split(" ").pop() ?? "";
        const bSurname = (b.personFullName ?? "").split(" ").pop() ?? "";
        return aSurname.localeCompare(bSurname, "pl");
      });
    const vacant = all
      .filter((p) => !p.personFullName)
      .sort((a, b) => a.position.localeCompare(b.position, "pl"));
    return [...filled, ...vacant];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTab, refreshKey]);

  const filteredOrgPeople = useMemo(() => {
    const q = searchOrg.trim().toLowerCase();
    if (!q) return orgPeople;
    return orgPeople.filter(
      (p) =>
        (p.personFullName ?? "").toLowerCase().includes(q) ||
        p.position.toLowerCase().includes(q) ||
        (p.unit ?? "").toLowerCase().includes(q),
    );
  }, [orgPeople, searchOrg]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const handleOrgUpdate = (id: string, patch: Partial<OrgNode>) => {
    const next = updateNode(orgTree, id, (n) => ({ ...n, ...patch }));
    setOrgTree(next);
    saveTree(next);
    refresh();
  };

  const handleOrgRemove = (id: string) => {
    const next = removeNode(orgTree, id);
    setOrgTree(next);
    saveTree(next);
    setSelectedOrgPersonId(null);
    refresh();
  };

  const handleOrgMove = (targetId: string) => {
    if (!moveNodeId) return;
    const moved = moveNodeFn(orgTree, moveNodeId, targetId);
    setOrgTree(moved);
    saveTree(moved);
    setMoveNodeId(null);
    refresh();
  };

  const openOrgPerson = (id: string) => {
    setOrgTree(loadTree());
    setSelectedOrgPersonId(id);
  };

  const closeOrgPerson = () => {
    if (selectedOrgPersonId) {
      const node = findNode(orgTree, selectedOrgPersonId);
      if (node?.type === "position") {
        const parent = findParent(orgTree, selectedOrgPersonId);
        if (parent) handleOrgUpdate(parent.id, { expanded: false });
      }
    }
    setSelectedOrgPersonId(null);
    setMoveNodeId(null);
  };

  const updateStatus = (id: string, status: MemberStatus) => {
    setOrgPersonStatus(id, status);
    refresh();
  };

  // Mapy ID → nazwa dla jednostek i stanowisk ze StructureEditor
  const structureNodeMap = useMemo(() => {
    const map = new Map<string, string>();
    flattenStructure(loadStructure()).forEach((u) => map.set(u.id, u.name));
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const highlightMatch = (text: string, query: string): React.ReactNode => {
    const q = query.trim();
    if (!q) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark
          style={{
            background: "#fff176",
            color: "inherit",
            borderRadius: 2,
            padding: 0,
          }}
        >
          {text.slice(idx, idx + q.length)}
        </mark>
        {text.slice(idx + q.length)}
      </>
    );
  };

  const getPersonMeta = (
    person: ReturnType<typeof getOrgChartPositions>[0],
  ): string => {
    const parts: string[] = [];
    if (person.unitStructureId) {
      const unitName = structureNodeMap.get(person.unitStructureId);
      if (unitName) parts.push(unitName);
    }
    if (person.positionId) {
      const posName =
        structureNodeMap.get(person.positionId) ?? person.positionId;
      parts.push(posName);
    }
    return parts.join(" · ");
  };

  const { mutate: sendInvite } = workspaceService.useInviteToWorkspace();
  const { mutate: syncPersons, isLoading: isSyncing } =
    workspaceService.useSyncOrgPersons();

  const handleSyncToDb = () => {
    if (!workspaceId) {
      notification({
        text: "Nie można określić przestrzeni roboczej. Upewnij się, że masz założoną firmę.",
        title: "Błąd",
        type: ENotificationType.ERROR,
      });
      return;
    }
    const supervisors = getOrgChartSupervisorMap();
    const persons = getOrgChartPositions()
      .filter((p) => !!p.personFullName)
      .map((p) => ({
        localId: p.id,
        fullName: p.personFullName ?? "",
        positionTitle: p.position,
        email: p.email,
        gradeId: p.gradeId,
        unitStructureLocalId: p.unitStructureId,
        positionStructureLocalId: p.positionId,
        supervisorLocalId: supervisors.get(p.id),
        memberStatus: p.memberStatus,
      }));

    syncPersons(
      { WorkspaceId: workspaceId, Persons: persons },
      {
        onSuccess: () => {
          notification({
            text: `Zapisano ${persons.length} ${
              persons.length === 1
                ? "osobę"
                : persons.length < 5
                ? "osoby"
                : "osób"
            } w bazie danych.`,
            title: "Zapisano",
            type: ENotificationType.SUCCESS,
          });
        },
        onError: (e: {
          response?: { data?: { errorCodes?: Array<{ message: string }> } };
        }) => {
          notification({
            text:
              e.response?.data?.errorCodes?.[0]?.message ??
              "Nie udało się zapisać listy osób.",
            title: "Błąd zapisu",
            type: ENotificationType.ERROR,
          });
        },
      },
    );
  };

  const handleInviteClick = (
    person: ReturnType<typeof getOrgChartPositions>[0],
  ) => {
    console.log(
      "[Invite] kliknięto dla:",
      person.personFullName,
      "| email:",
      person.email,
      "| workspaceId:",
      workspace?.Id,
      "| role count:",
      workspaceRoles.length,
    );

    const email = person.email?.trim();
    if (!email) {
      notification({
        text: "Uzupełnij adres e-mail w karcie osoby, aby wysłać zaproszenie.",
        title: "Brak adresu e-mail",
        type: ENotificationType.ERROR,
      });
      return;
    }
    if (!workspaceId) {
      notification({
        text: "Nie można określić przestrzeni roboczej. Upewnij się, że masz założoną firmę.",
        title: "Błąd zaproszenia",
        type: ENotificationType.ERROR,
      });
      return;
    }
    const defaultRole =
      workspaceRoles.find((r) => r.StaticType !== "owner") ?? workspaceRoles[0];
    if (!defaultRole) {
      notification({
        text: "Brak dostępnych ról w zespole. Poczekaj na załadowanie danych zespołu i spróbuj ponownie.",
        title: "Błąd zaproszenia",
        type: ENotificationType.ERROR,
      });
      return;
    }

    console.log("[Invite] wysyłam:", {
      WorkspaceId: workspaceId,
      RoleId: defaultRole.Id,
      Email: email,
      Name: person.personFullName,
    });

    sendInvite(
      {
        WorkspaceId: workspaceId,
        RoleId: defaultRole.Id,
        Email: email,
        Name: person.personFullName,
      },
      {
        onSuccess: () => {
          console.log("[Invite] sukces dla:", person.personFullName);
          updateStatus(person.id, "aktywuje");
          refetch();
        },
        onError: (e: {
          response?: { data?: { errorCodes?: Array<{ message: string }> } };
        }) => {
          const msg =
            e.response?.data?.errorCodes?.[0]?.message ??
            "Nie udało się wysłać zaproszenia.";
          console.error("[Invite] błąd:", e.response?.data, "| msg:", msg);
          notification({
            text: msg,
            title: "Błąd zaproszenia",
            type: ENotificationType.ERROR,
          });
        },
      },
    );
  };

  const closeAddWorker = () => {
    setShowNewAdd(false);
    setInvitePersonId(null);
  };

  const invitePerson = orgPeople.find((p) => p.id === invitePersonId);

  // Intercept WorkspaceMembers' showAdd: when it wants to open old AddWorker, open new one instead
  const handleWorkspaceMembersSetShowAdd = (val: boolean) => {
    if (val) setShowNewAdd(true);
  };

  const handleCreateWorkspace = (newWorkspaceId: string) => {
    setWorkspaceIdOverride(newWorkspaceId);
  };

  const showAddWorker = showNewAdd || !!invitePersonId;

  if (!workspaceId) {
    return <CreateWorkspace onCreate={handleCreateWorkspace} />;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.title}>
        <TeamIcon />
        {t("roles.team")}
      </div>

      {/* Sub-tab navigation */}
      <div className={styles.subTabs}>
        <div
          className={`${styles.subTab} ${
            subTab === "members" ? styles.subTabActive : ""
          }`}
          onClick={() => setSubTab("members")}
        >
          Lista osób
        </div>
        <div
          className={`${styles.subTab} ${
            subTab === "structure" ? styles.subTabActive : ""
          }`}
          onClick={() => setSubTab("structure")}
        >
          Struktura
        </div>
        <div
          className={`${styles.subTab} ${
            subTab === "privileges" ? styles.subTabActive : ""
          }`}
          onClick={() => setSubTab("privileges")}
        >
          Uprawnienia
        </div>
        <div
          className={`${styles.subTab} ${
            subTab === "kadry" ? styles.subTabActive : ""
          }`}
          onClick={() => setSubTab("kadry")}
        >
          Stopnie/Stanowiska
        </div>
      </div>

      {subTab === "members" && (
        <div className={styles.membersContent}>
          <>
            {isLoadingWorkspace && <Spinner />}
            {!isLoadingWorkspace && workspaceId && (
              <>
                {canInvite && (
                  <div className={styles.membersAddRow}>
                    <button
                      className={styles.addPersonBtn}
                      onClick={() => setShowNewAdd(true)}
                    >
                      + Dodaj członka zespołu
                    </button>
                  </div>
                )}
                {/* showAdd={false} prevents WorkspaceMembers from rendering its own AddWorker;
                    setShowAdd is intercepted to open our new expanded AddWorker instead */}
                <WorkspaceMembers
                  workspace={workspace}
                  showAdd={false}
                  setShowAdd={handleWorkspaceMembersSetShowAdd}
                  refetch={refetch}
                  myPrivilages={myPrivilages}
                  roles={workspaceRoles}
                  members={workspaceMembers.filter(
                    (x) => !x.IsMe || !isLegalPerson,
                  )}
                />
              </>
            )}
          </>

          {/* Org chart people with status system */}
          <div
            className={`${styles.orgPeopleSection} ${
              selectedOrgPersonId || showAddPersonPanel
                ? styles.orgPeopleSectionWithPanel
                : ""
            }`}
          >
            <div className={styles.orgPeopleListWrapper}>
              <div className={styles.orgPeopleHeader}>
                <span className={styles.orgPeopleTitle}>
                  Osoby ze struktury organizacyjnej
                </span>
                <TextField
                  size="small"
                  placeholder="Szukaj"
                  value={searchOrg}
                  onChange={(e) => setSearchOrg(e.target.value)}
                  className={styles.orgSearchBox}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {searchOrg ? (
                          <button
                            className={styles.searchClearBtn}
                            onClick={() => setSearchOrg("")}
                            tabIndex={-1}
                          >
                            ×
                          </button>
                        ) : (
                          <SearchIcon
                            fontSize="small"
                            style={{ fontSize: 16, color: "#9e9e9e" }}
                          />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
                <button
                  className={styles.addPersonBtn}
                  onClick={() => {
                    setShowAddPersonPanel(true);
                    setSelectedOrgPersonId(null);
                  }}
                >
                  + Nowa osoba
                </button>
                <button
                  className={styles.saveToDbBtn}
                  onClick={handleSyncToDb}
                  disabled={isSyncing}
                  title="Zapisz listę osób do bazy danych"
                >
                  {isSyncing ? "Zapisuję…" : "Zapisz w bazie"}
                </button>
              </div>

              {filteredOrgPeople.length === 0 ? (
                <div className={styles.orgPeopleEmpty}>
                  {searchOrg
                    ? "Brak wyników wyszukiwania."
                    : "Brak osób w strukturze organizacyjnej."}
                </div>
              ) : (
                <div className={styles.orgPeopleList}>
                  {filteredOrgPeople.map((person) => {
                    const isVacant = !person.personFullName;
                    const cfg = isVacant
                      ? null
                      : STATUS_CONFIG[person.memberStatus];
                    const isSelected = selectedOrgPersonId === person.id;
                    return (
                      <div
                        key={person.id}
                        className={`${styles.orgPersonRow} ${
                          person.memberStatus === "archiwalny"
                            ? styles.orgPersonRowArchived
                            : ""
                        } ${isVacant ? styles.orgPersonRowVacant : ""} ${
                          isSelected ? styles.orgPersonRowSelected : ""
                        }`}
                        onClick={() => !isVacant && openOrgPerson(person.id)}
                        style={{ cursor: isVacant ? "default" : "pointer" }}
                      >
                        <div className={styles.orgPersonMain}>
                          <div
                            className={`${styles.orgPersonName} ${
                              isVacant ? styles.orgPersonNameVacant : ""
                            }`}
                          >
                            {isVacant
                              ? "Wakat"
                              : highlightMatch(
                                  person.personFullName ?? "",
                                  searchOrg,
                                )}
                          </div>
                          {!isVacant &&
                            (() => {
                              const meta = getPersonMeta(person);
                              return meta ? (
                                <div className={styles.orgPersonMeta}>
                                  {highlightMatch(meta, searchOrg)}
                                </div>
                              ) : null;
                            })()}
                        </div>
                        <div className={styles.orgPersonActions}>
                          {!isVacant && person.memberStatus === "wpisany" ? (
                            <button
                              className={styles.actionBtn}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleInviteClick(person);
                              }}
                            >
                              + Zaproś do zespołu
                            </button>
                          ) : (
                            !isVacant &&
                            cfg &&
                            person.memberStatus !== "wpisany" && (
                              <span
                                className={styles.statusBadge}
                                style={{ background: cfg.bg, color: cfg.color }}
                              >
                                {cfg.label}
                              </span>
                            )
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {showAddPersonPanel && (
              <AddPersonPanel
                onClose={() => setShowAddPersonPanel(false)}
                onSuccess={refresh}
              />
            )}

            {!showAddPersonPanel &&
              selectedOrgPersonId &&
              (() => {
                const selectedNode = findNode(orgTree, selectedOrgPersonId);
                if (!selectedNode) return null;
                const allPeople = collectPeople(orgTree);
                const supervisors =
                  orgTree.length > 0
                    ? orgTree[0].children.filter((c) => c.type === "position")
                    : [];
                return (
                  <EditPanel
                    node={selectedNode}
                    supervisors={supervisors}
                    allPeople={allPeople}
                    onUpdate={(patch) =>
                      handleOrgUpdate(selectedOrgPersonId, patch)
                    }
                    onDelete={() => handleOrgRemove(selectedOrgPersonId)}
                    onInvite={() => {
                      setInvitePersonId(selectedOrgPersonId);
                      setSelectedOrgPersonId(null);
                    }}
                    onMove={() => setMoveNodeId(selectedOrgPersonId)}
                    onClose={closeOrgPerson}
                  />
                );
              })()}
          </div>

          {/* New expanded AddWorker — handles both org chart add and workspace invite */}
          {showAddWorker && (
            <AddWorker
              roles={workspaceRoles}
              toggle={closeAddWorker}
              refetch={refetch}
              workspaceId={workspace?.Id ?? ""}
              initialName={invitePerson?.personFullName ?? ""}
              initialEmail={invitePerson?.email ?? ""}
              onSuccess={() => {
                if (invitePersonId) {
                  updateStatus(invitePersonId, "aktywuje");
                }
                refresh();
              }}
            />
          )}

          {moveNodeId && (
            <MoveDialog
              nodeName={findNode(orgTree, moveNodeId)?.name ?? ""}
              units={collectUnits(orgTree).filter((u) => u.id !== moveNodeId)}
              onMove={handleOrgMove}
              onClose={() => setMoveNodeId(null)}
            />
          )}
        </div>
      )}

      {subTab === "structure" && (
        <div className={styles.orgChartWrapper}>
          <StructureEditor />
        </div>
      )}

      {subTab === "privileges" && (
        <div className={styles.orgChartWrapper}>
          <PrivilegesTab members={workspaceMembers} me={me} />
        </div>
      )}

      {subTab === "orgchart" && (
        <div className={styles.orgChartWrapper}>
          <OrgChart onInvite={() => setSubTab("members")} />
        </div>
      )}

      {subTab === "kadry" && (
        <div className={styles.orgChartWrapper}>
          <KadroweTab />
        </div>
      )}
    </div>
  );
};

export default WorkersTab;
